import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from '../../i18n';
import { Get, Send, Update, Remove } from '../../services/service.api';
import { toast } from 'react-toastify';
import {
  Plus, Trash2, Edit, Mail, Phone,
  Building2, X, Lock, Camera, UserPlus, Search, CheckCircle
} from 'lucide-react';
import ConfirmModal from '../../components/component.confirmModal';

function PartnerModal({ partner, companies, onClose, onSave, t }) {
  const fileInputRef = useRef(null);
  const [form, setForm] = useState({
    nom: partner?.nom || '',
    prenom: partner?.prenom || '',
    email: partner?.email || '',
    tel: partner?.tel || '',
    password: '',
    entreprise_id: partner?.entreprise_id || '',
    photo: partner?.photo || '',
    percentage: partner?.percentage || 0,
  });

  const handlePhotoUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      return toast.error('L\'image est trop volumineuse (max 5 Mo)');
    }
    const reader = new FileReader();
    reader.onload = (uploadEvent) => {
      setForm(prev => ({ ...prev, photo: uploadEvent.target.result }));
    };
    reader.readAsDataURL(file);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.nom.trim() || !form.prenom.trim() || !form.email.trim()) {
      return toast.error('Nom, prénom et email sont obligatoires');
    }
    if (!partner && (!form.password || form.password.length < 6)) {
      return toast.error('Le mot de passe doit comporter au moins 6 caractères');
    }
    onSave(form);
  };

  const initials = `${(form.prenom || 'P')[0] || ''}${(form.nom || '')[0] || ''}`.toUpperCase();

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-box" style={{ maxWidth: 560 }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{
              width: 36, height: 36, borderRadius: 'var(--radius)',
              background: 'rgba(20, 184, 166, 0.15)', color: 'var(--primary)',
              display: 'flex', alignItems: 'center', justifyContent: 'center'
            }}>
              <UserPlus size={20} />
            </div>
            <div>
              <h2 className="modal-title">
                {partner ? t.partners.edit : t.partners.createPartnerTitle}
              </h2>
              <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>
                {partner ? 'Modifier les coordonnées' : t.partners.createPartnerSubtitle}
              </div>
            </div>
          </div>
          <button className="modal-close" onClick={onClose}><X size={16} /></button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            
            {/* Photo Avatar */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '8px 0' }}>
              {form.photo ? (
                <img
                  src={form.photo}
                  alt="Avatar"
                  style={{
                    width: 64, height: 64, borderRadius: '50%',
                    objectFit: 'cover', border: '2px solid var(--primary)'
                  }}
                />
              ) : (
                <div style={{
                  width: 64, height: 64, borderRadius: '50%',
                  background: 'linear-gradient(135deg, var(--primary), var(--gold))',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontWeight: 800, fontSize: '1.4rem', color: 'white'
                }}>
                  {initials}
                </div>
              )}
              <div>
                <input
                  type="file"
                  ref={fileInputRef}
                  accept="image/*"
                  onChange={handlePhotoUpload}
                  style={{ display: 'none' }}
                />
                <div style={{ display: 'flex', gap: 8 }}>
                  <button
                    type="button"
                    className="btn btn-outline btn-sm"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <Camera size={14} /> {form.photo ? 'Changer photo' : 'Ajouter photo'}
                  </button>
                  {form.photo && (
                    <button
                      type="button"
                      className="btn btn-danger btn-sm"
                      onClick={() => setForm(p => ({ ...p, photo: '' }))}
                    >
                      <Trash2 size={14} />
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Identité */}
            <div className="grid-2">
              <div className="form-group">
                <label className="form-label">{t.auth.prenom} <span className="required">*</span></label>
                <input
                  className="form-control"
                  value={form.prenom}
                  required
                  onChange={e => setForm({ ...form, prenom: e.target.value })}
                  placeholder="Prénom du partenaire"
                />
              </div>
              <div className="form-group">
                <label className="form-label">{t.auth.nom} <span className="required">*</span></label>
                <input
                  className="form-control"
                  value={form.nom}
                  required
                  onChange={e => setForm({ ...form, nom: e.target.value })}
                  placeholder="Nom de famille"
                />
              </div>
            </div>

            {/* Email & Téléphone */}
            <div className="grid-2">
              <div className="form-group">
                <label className="form-label">
                  <Mail size={13} style={{ display: 'inline', marginRight: 4 }} />
                  {t.auth.email} <span className="required">*</span>
                </label>
                <input
                  type="email"
                  className="form-control"
                  value={form.email}
                  required
                  disabled={!!partner}
                  onChange={e => setForm({ ...form, email: e.target.value })}
                  placeholder="partenaire@domaine.com"
                />
              </div>
              <div className="form-group">
                <label className="form-label">
                  <Phone size={13} style={{ display: 'inline', marginRight: 4 }} />
                  {t.auth.tel}
                </label>
                <input
                  type="tel"
                  className="form-control"
                  value={form.tel}
                  onChange={e => setForm({ ...form, tel: e.target.value })}
                  placeholder="+226 XX XX XX XX"
                />
              </div>
            </div>

            {/* Entreprise affiliée */}
            <div className="form-group">
              <label className="form-label">
                <Building2 size={13} style={{ display: 'inline', marginRight: 4 }} />
                {t.partners.company}
              </label>
              <select
                className="form-control"
                value={form.entreprise_id}
                onChange={e => setForm({ ...form, entreprise_id: e.target.value })}
              >
                <option value="">-- Sans entreprise affiliée --</option>
                {companies.map(c => (
                  <option key={c.id} value={c.id}>
                    {c.libelle} {c.ville ? `(${c.ville})` : ''}
                  </option>
                ))}
              </select>
            </div>

            {/* Mot de passe (requis à la création, optionnel à la modif) */}
            <div className="form-group">
              <label className="form-label">
                <Lock size={13} style={{ display: 'inline', marginRight: 4 }} />
                {partner ? 'Nouveau mot de passe (optionnel)' : t.partners.password} {!partner && <span className="required">*</span>}
              </label>
              <input
                type="password"
                className="form-control"
                value={form.password}
                required={!partner}
                onChange={e => setForm({ ...form, password: e.target.value })}
                placeholder="Au moins 6 caractères"
              />
              <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: 4 }}>
                {partner
                  ? 'Laissez vide pour conserver le mot de passe actuel'
                  : 'Ce mot de passe permettra au partenaire de se connecter.'}
              </div>
            </div>

            {/* Pourcentage (Parts) */}
            <div className="form-group">
              <label className="form-label">
                Pourcentage des parts (%)
              </label>
              <input
                type="number"
                min="0"
                max="100"
                step="0.01"
                className="form-control"
                value={form.percentage}
                onChange={e => setForm({ ...form, percentage: e.target.value })}
                placeholder="Ex: 10"
              />
              <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: 4 }}>
                Sera utilisé pour calculer la part des bénéfices de ce partenaire.
              </div>
            </div>

          </div>

          <div className="modal-footer">
            <button type="button" className="btn btn-outline" onClick={onClose}>
              {t.common.cancel}
            </button>
            <button type="submit" className="btn btn-primary">
              {t.common.save}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function Partners() {
  const { t } = useTranslation();
  const token = useSelector(s => s.userReducer.token);
  const [partners, setPartners] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  
  const [showModal, setShowModal] = useState(false);
  const [selectedPartner, setSelectedPartner] = useState(null);
  const [deleteId, setDeleteId] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [resPartners, resCompanies] = await Promise.all([
        Get('/users/partners', token),
        Get('/companies', token),
      ]);
      if (Array.isArray(resPartners)) setPartners(resPartners);
      if (Array.isArray(resCompanies)) setCompanies(resCompanies);
    } catch (err) {
      console.error(err);
      toast.error('Erreur chargement partenaires');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (token) fetchData();
  }, [token, fetchData]);

  const handleSave = async (form) => {
    try {
      let res;
      if (selectedPartner) {
        res = await Update(`/users/partners/${selectedPartner.id}`, form, token);
      } else {
        res = await Send('/users/partners', form, true, token);
      }

      if (res?.error) {
        return toast.error(res.error);
      }

      toast.success(selectedPartner ? 'Partenaire mis à jour ✓' : 'Nouveau partenaire créé ✓');
      setShowModal(false);
      setSelectedPartner(null);
      fetchData();
    } catch (err) {
      toast.error('Une erreur est survenue');
    }
  };

  const confirmDelete = async () => {
    if (!deleteId) return;
    try {
      const res = await Remove(`/users/${deleteId}`, token);
      if (res?.error) return toast.error(res.error);
      toast.success('Partenaire retiré');
      setDeleteId(null);
      fetchData();
    } catch {
      toast.error('Erreur suppression');
    }
  };

  const handleApprove = async (partnerId) => {
    try {
      const res = await Update(`/users/${partnerId}/approve`, {}, token);
      if (res?.error) return toast.error(res.error);
      toast.success('Partenaire approuvé ✓ Il peut maintenant se reconnecter.');
      fetchData();
    } catch {
      toast.error('Erreur lors de l\'approbation');
    }
  };

  const getCompanyName = (companyId) => {
    if (!companyId) return null;
    const found = companies.find(c => c.id === companyId);
    return found ? found.libelle : companyId;
  };

  const filtered = partners.filter(p =>
    (`${p.nom || ''} ${p.prenom || ''} ${p.email || ''} ${getCompanyName(p.entreprise_id) || ''}`)
      .toLowerCase()
      .includes(search.toLowerCase())
  );

  return (
    <div className="page-content page-enter">
      {/* Header */}
      <div className="page-header">
        <div>
          <h1>{t.partners.title}</h1>
          <p>
            {partners.filter(p => p.status !== 'pending').length} approuvé(s)
            {partners.filter(p => p.status === 'pending').length > 0 && (
              <span style={{
                marginLeft: 10, background: '#f59e0b',
                color: '#000', padding: '2px 8px', borderRadius: 12,
                fontSize: '0.75rem', fontWeight: 700,
              }}>
                ⏳ {partners.filter(p => p.status === 'pending').length} en attente
              </span>
            )}
          </p>
        </div>
        <button
          className="btn btn-primary"
          onClick={() => { setSelectedPartner(null); setShowModal(true); }}
        >
          <Plus size={16} /> {t.partners.add}
        </button>
      </div>

      {/* Barre de Recherche */}
      <div className="search-wrapper mb-4" style={{ maxWidth: 360 }}>
        <Search size={16} />
        <input
          className="form-control search-input"
          placeholder={t.common.search}
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
      </div>

      {/* Liste des partenaires */}
      {loading ? (
        <div className="grid-3">
          {[...Array(3)].map((_, i) => <div key={i} className="skeleton" style={{ height: 180 }} />)}
        </div>
      ) : filtered.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon">🤝</div>
          <h3>{t.partners.noPartners}</h3>
          <p>Cliquez sur "{t.partners.add}" pour ajouter votre premier partenaire.</p>
          <button
            className="btn btn-primary mt-3"
            onClick={() => { setSelectedPartner(null); setShowModal(true); }}
          >
            <Plus size={16} /> {t.partners.add}
          </button>
        </div>
      ) : (
        <div className="grid-3">
          {filtered.map(p => {
            const initials = `${(p.prenom || '?')[0]}${(p.nom || '?')[0]}`.toUpperCase();
            const companyName = getCompanyName(p.entreprise_id);

            return (
              <div key={p.id} className="card" style={{ display: 'flex', flexDirection: 'column', gap: 14, position: 'relative' }}>
                {/* Header Carte : Photo + Nom + Badge */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                  {p.photo ? (
                    <img
                      src={p.photo}
                      alt={`${p.prenom} ${p.nom}`}
                      style={{
                        width: 52, height: 52, borderRadius: '50%',
                        objectFit: 'cover', border: '2px solid var(--primary)',
                        flexShrink: 0
                      }}
                    />
                  ) : (
                    <div style={{
                      width: 52, height: 52, borderRadius: '50%',
                      background: 'linear-gradient(135deg, var(--primary), var(--gold))',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontWeight: 800, fontSize: '1.15rem', color: 'white', flexShrink: 0
                    }}>
                      {initials}
                    </div>
                  )}

                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{
                      fontWeight: 700, fontSize: '1.05rem', color: 'var(--text-primary)',
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'
                    }}>
                      {p.prenom} {p.nom}
                    </div>
                    {p.status === 'pending' ? (
                      <span style={{
                        fontSize: '0.7rem', marginTop: 3, display: 'inline-block',
                        background: 'rgba(245, 158, 11, 0.15)', color: '#f59e0b',
                        padding: '2px 8px', borderRadius: 12, border: '1px solid rgba(245,158,11,0.3)',
                      }}>
                        ⏳ En attente
                      </span>
                    ) : (
                      <span className="badge badge-primary" style={{ fontSize: '0.7rem', marginTop: 3 }}>
                        ✓ Approuvé
                      </span>
                    )}
                  </div>
                </div>

                <div style={{ padding: '8px 12px', background: 'rgba(20, 184, 166, 0.05)', borderRadius: 'var(--radius)', borderLeft: '3px solid var(--primary)', marginBottom: 8 }}>
                  <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', fontWeight: 600 }}>Part des bénéfices</div>
                  <div style={{ fontWeight: 700, color: 'var(--primary)', fontSize: '1.1rem' }}>
                    {p.percentage || 0}%
                  </div>
                </div>

                {/* Coordonnées & Entreprise */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, fontSize: '0.875rem' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--text-muted)' }}>
                    <Mail size={14} color="var(--primary)" />
                    <span className="truncate">{p.email}</span>
                  </div>
                  {p.tel && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--text-muted)' }}>
                      <Phone size={14} color="var(--primary)" />
                      <span>{p.tel}</span>
                    </div>
                  )}
                  {companyName && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--text-muted)' }}>
                      <Building2 size={14} color="var(--gold)" />
                      <span className="truncate" style={{ fontWeight: 600, color: 'var(--text-primary)' }}>
                        {companyName}
                      </span>
                    </div>
                  )}
                </div>

                {/* Actions */}
                <div style={{
                  borderTop: '1px solid var(--border)', paddingTop: 12,
                  display: 'flex', justifyContent: 'flex-end', gap: 8, flexWrap: 'wrap'
                }}>
                  {p.status === 'pending' && (
                    <button
                      className="btn btn-primary btn-sm"
                      onClick={() => handleApprove(p.id)}
                      title="Approuver ce partenaire"
                      style={{ gap: 6, display: 'flex', alignItems: 'center' }}
                    >
                      <CheckCircle size={14} /> Approuver
                    </button>
                  )}
                  <button
                    className="btn btn-outline btn-sm"
                    onClick={() => { setSelectedPartner(p); setShowModal(true); }}
                    title={t.common.edit}
                  >
                    <Edit size={14} /> {t.common.edit}
                  </button>
                  <button
                    className="btn btn-danger btn-sm"
                    onClick={() => setDeleteId(p.id)}
                    title={t.partners.remove}
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Modal Création / Edition */}
      {showModal && (
        <PartnerModal
          partner={selectedPartner}
          companies={companies}
          onClose={() => { setShowModal(false); setSelectedPartner(null); }}
          onSave={handleSave}
          t={t}
        />
      )}

      <ConfirmModal
        isOpen={!!deleteId}
        title="Retirer ce partenaire"
        message="Êtes-vous sûr de vouloir retirer ce partenaire ? Il n'aura plus accès à la plateforme."
        onConfirm={confirmDelete}
        onCancel={() => setDeleteId(null)}
      />
    </div>
  );
}
